import { useState } from 'react'

import { gasVolume, ru, waterVolume } from '../../lib/physics'
import { Choice, Predict, Slider } from './common'

/**
 * Сжимаем шприц (урок 11): воздух под поршнем сжимается в разы и, отпущенный, возвращает
 * поршень, — между молекулами газа большие промежутки. Воду той же силой не сжать, а пузырёк
 * воздуха в ней уменьшается: промежутки между молекулами воды малы.
 */

type Что = 'воздух' | 'вода' | 'вода с пузырьком'

const V0 = 20 // мл
const ПУЗЫРЁК = 2 // мл воздуха в воде
const S = Math.PI * 0.01 ** 2 // поршень диаметром 2 см, м²
const X0 = 60 // левый край ствола, px
const PX_PER_ML = 11

export default function SyringeLab() {
  const [что, setЧто] = useState<Что>('воздух')
  const [F, setF] = useState(0)

  const газ = что === 'воздух' ? V0 : что === 'вода с пузырьком' ? ПУЗЫРЁК : 0
  const вода = V0 - газ
  const V = gasVolume(газ, F, S) + waterVolume(вода, F, S)
  const поршень = X0 + V * PX_PER_ML
  const пузырь = что === 'вода с пузырьком' ? gasVolume(ПУЗЫРЁК, F, S) : 0
  const rПуз = Math.sqrt(пузырь) * 9

  return (
    <div className="card">
      <svg className="lab-svg" viewBox="0 0 360 130" role="img" aria-label="Закрытый шприц под нагрузкой">
        {/* ствол и закрытый носик */}
        <rect x={X0 - 14} y={56} width={14} height={18} rx={3} className="fill-muted ink" strokeWidth={1.5} />
        <rect x={X0} y={40} width={V0 * PX_PER_ML + 20} height={50} rx={4} className="fill-paper ink" strokeWidth={1.5} />
        <rect x={X0 + 1} y={41} width={V - 1 > 0 ? V * PX_PER_ML - 1 : 0} height={48} className={что === 'воздух' ? 'fill-light' : 'fill-cold'} />
        {пузырь > 0 && <circle cx={X0 + 30} cy={65} r={rПуз} className="fill-paper ink-muted" />}
        {[0, 5, 10, 15, 20].map((мл) => (
          <g key={мл}>
            <line x1={X0 + мл * PX_PER_ML} y1={90} x2={X0 + мл * PX_PER_ML} y2={98} className="ink-muted" />
            <text x={X0 + мл * PX_PER_ML} y={110} textAnchor="middle" className="lab-tick">{мл}</text>
          </g>
        ))}
        <text x={X0 + V0 * PX_PER_ML + 24} y={110} className="lab-tick">мл</text>
        {/* поршень и шток */}
        <rect x={поршень} y={42} width={8} height={46} className="fill-ink" />
        <rect x={поршень + 8} y={60} width={340 - поршень} height={10} className="fill-muted ink" strokeWidth={1} />
        {F > 0 && <text x={330} y={34} textAnchor="end" className="lab-label heavy">F = {F} Н →</text>}
      </svg>

      <div className="lab-readout">
        <span>объём под поршнем: <b>{ru(V, газ > 0 ? 1 : 3)} мл</b></span>
        {пузырь > 0 && <span>пузырёк: {ru(пузырь, 2)} мл из {ПУЗЫРЁК}</span>}
        {что !== 'воздух' && <span>вода: −{ru(вода - waterVolume(вода, F, S), 4)} мл</span>}
      </div>

      <div className="lab-sub">Что в шприце</div>
      <Choice options={(['воздух', 'вода', 'вода с пузырьком'] as const).map((v) => ({ value: v, label: v }))} value={что} onChange={setЧто} />
      <Slider label="Давим на поршень" value={F} min={0} max={100} step={5} unit="Н" onChange={setF} />
      <button className="btn" onClick={() => setF(0)} disabled={F === 0}>Отпустить поршень</button>

      <Predict
        question="Нальём в шприц воду и надавим так же сильно. Насколько она сожмётся?"
        options={['так же, как воздух', 'вдвое меньше', 'почти нисколько']}
        right={2}
        revealed={что !== 'воздух' && F >= 50}
        resetKey={что}
        explain="Сто ньютонов сжимают воздух вчетверо, а воду — на тысячные доли миллилитра. Между молекулами газа большие промежутки, между молекулами воды — малые. Пузырёк же воздуха в воде сжимается так же легко, как воздух."
      />
      <p className="muted small">
        Отпусти поршень — сжатый воздух толкает его обратно. Совсем несжимаемой вода не бывает: иначе в ней не
        распространялся бы звук, а под водой стук камней слышно.
      </p>
    </div>
  )
}
